export * from './lib/userProfileService';

import type { PersonalInfoFormData } from '@acme/domains';
import {
  personalInfoFormConfig,
  type PersonalInfoFormResult,
  type UpdateProfileModel,
} from '@acme/domains';
import {
  FormControls,
  useFormModel,
  type FieldConfig,
  type FormConfig,
} from '@acme/foundation';
import { forwardRef, useMemo } from 'react';

export type UserGeneralFormDefaultData = {
  person?: {
    name?: string | null;
    snils?: string | null;
    inn?: string | null;
    title?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    passport?: {
      seriesNumber?: string | null;
      dateIssue?: string | null;
      issued?: string | null;
    } | null;
  } | null;
};

export type UserGeneralFormRefImperativeHandleType = {
  onChangeControler: ({
    name,
    value,
  }: {
    name: string;
    value: string | Blob;
  }) => void;
};

interface UserGeneralFormProps {
  onError?: (error: string) => void;
  defaultData?: UserGeneralFormDefaultData | null;
  errorsForm?: string[];
  includeUnchangedFields?: boolean;
  isEdit: boolean;
  formRef?: React.RefObject<{ submit: () => Promise<void> }>;
  updateProfile: UpdateProfileModel;
}

export const UserGeneralForm = forwardRef<
  UserGeneralFormRefImperativeHandleType,
  UserGeneralFormProps
>(
  (
    { onError, defaultData, errorsForm, isEdit, formRef, updateProfile },
    _ref
  ) => {
    void errorsForm;

    const formConfig = useMemo(() => {
      const config = personalInfoFormConfig(
        true
      ) as unknown as FormConfig<PersonalInfoFormResult>;
      config.controls.forEach(
        (control: FieldConfig<PersonalInfoFormResult>) => {
          if (!control.rules) control.rules = [];
          control.rules.push({
            when: () => !isEdit,
            set: { disabled: true },
          });
        }
      );
      return config;
    }, [isEdit]);

    const form = useFormModel<PersonalInfoFormResult>({
      config: formConfig,
      ctx: (defaultData || {}) as unknown as PersonalInfoFormResult,
    });

    if (formRef) {
      (
        formRef as React.MutableRefObject<{
          submit: () => Promise<void>;
        } | null>
      ).current = {
        submit: async () => {
          return form.submit(async (finalModel: PersonalInfoFormResult) => {
            try {
              const formData: PersonalInfoFormData = {
                name: finalModel.person.name,
                email: finalModel.person.email,
                phoneNumber: finalModel.person.phoneNumber,
                inn: finalModel.person.inn,
                title: finalModel.person.title,
                passport: {
                  seriesNumber: finalModel.person.passport.seriesNumber,
                  dateIssue: finalModel.person.passport.dateIssue,
                  issued: finalModel.person.passport.issued,
                },
              };

              await updateProfile(formData);
            } catch (error) {
              onError?.(
                error instanceof Error ? error.message : 'Ошибка при сохранении'
              );
              throw error;
            }
          });
        },
      };
    }

    return (
      <div className="grid grid-cols-3 gap-6">
        <FormControls form={form} />
      </div>
    );
  }
);
